import { CollapsibleFormSection } from '@dynamika-shared/ui/CollapsibleFormSection/CollapsibleFormSection';
import { TrashIcon } from '@dynamika-shared/ui/icons';
import { FC, useEffect, useState } from 'react';
import { Modal } from '@dynamika-shared/ui/Modal/Modal';
import { NoContent } from '@dynamika-shared/ui/NoContent/NoContent';
import { LabelMapper } from './LabelMapper';
import { SubPageHeader } from '@components/SubPageHeader/SubPageHeader';
import { ImagingLabelsControlSchemaType } from '../uiControlSchemaByStatus/ControlTypes';
import { uiControlSchemas } from '../uiControlSchemaByStatus/schemas';
import { StudyStatusEnum } from '@dynamika-shared/types/status';
import { useBreadcrumbs } from '@hooks/useBreadcrumbs';
import { useClinicalTrialSlice } from '@restore/clinicalTrial/clinicalTrial.slice';
import { useParams } from 'react-router-dom';
import { useImagingLabelsSlice } from '@restore/imagingLabels/imagingLabels.slice';
import { useAuthSlice } from '@restore/auth/auth.slice';
import { UserRoles } from '@dynamika-shared/types/auth';

type ImagingLabelRowProps = {
  title: string;
  action: () => void;
  showButton?: boolean;
};

const ImagingLabelRow: FC<ImagingLabelRowProps> = ({
  title,
  action,
  showButton = true,
}) => (
  <div
    role='listitem'
    data-testid='ImagingLabelRow'
    data-title={title}
    className='flex w-full justify-between border-b px-[4.2rem] py-2'
  >
    <span className='text-1624'>{title}</span>
    {showButton && (
      <button aria-label={title} onClick={action}>
        <TrashIcon className='h-3 w-3' />
      </button>
    )}
  </div>
);

export const ClinicalTrialImagingLabelsPage = () => {

  const isPM = useAuthSlice(s => s?.data?.selectedRole === UserRoles.PROJECT_MANAGER);

  const [labelForDel, setLabelForDel] = useState('');
  const [showDrawer, setShowDrawer] = useState(false);
  const [ctLoading, clinicalTrial] = useClinicalTrialSlice(state => [
    state.loading,
    state.data,
  ]);
  const [labelsLoading, unAssignLabels] = useImagingLabelsSlice(
    (state, actions) => [state.loading, actions.unAssignLabelsRequest]
  );
  const [uiControlSchema, setUiControlSchema] =
    useState<ImagingLabelsControlSchemaType>(
      uiControlSchemas[clinicalTrial?.status ?? StudyStatusEnum.draft]
        .imagingLabels
    );

  const { id: clinicalTrialId } = useParams<{
    id: string;
  }>();

  useBreadcrumbs(
    [
      ['Clinical Trials', 'clinicalTrials'],
      [clinicalTrial?.name, 'clinicalTrialGeneralInformation'],
      ['Labels', 'clinicalTrialImagingLabels'],
    ],
    [clinicalTrial?.id]
  );

  useEffect(() => {
    setLabelForDel('');
    setShowDrawer(false);
  }, [clinicalTrial, setShowDrawer]);

  const deleteLabel = async () => {
    if (clinicalTrialId) {
      unAssignLabels({ clinicalTrialId, labelId: labelForDel });
    }
  };
  useEffect(() => {
    if (clinicalTrial?.status) {
      const schema = uiControlSchemas[clinicalTrial?.status].imagingLabels;
      setUiControlSchema(schema);
    }
  }, [clinicalTrial?.status]);

  return (
    <div className='relative flex min-h-full w-full grow flex-col'>
      <SubPageHeader
        title='Imaging Labels'
        action={() => setShowDrawer(!showDrawer)}
        showButton={
          !uiControlSchema.addLabel.hidden &&
          !!clinicalTrial?.labelGroups?.length &&
          !isPM
        }
        buttonText='Label'
      />
      {clinicalTrial?.labelGroups?.length ? (
        <section>
          {clinicalTrial.labelGroups.map(({ modalityName, labels }) => (
            <CollapsibleFormSection
              key={modalityName}
              title={
                <div className='flex items-center gap-[6px]'>
                  {modalityName}
                  <div className='h-[5px] w-[5px] rounded-3xl bg-spanishGray'></div>
                  <div className='text-1424 font-normal text-dimGray'>
                    {`${labels.length}`}{' '}
                    {labels.length > 1 ? 'Labels' : 'Label'}
                  </div>
                </div>
              }
              styles={{ header: 'mb-0 flex-row-reverse justify-end gap-1' }}
              resize={labels.length}
            >
              {labels.map(label => (
                <div
                  className='mb-0 flex flex-row-reverse justify-end gap-1'
                  key={label.name}
                >
                  <ImagingLabelRow
                    title={label.name}
                    showButton={!uiControlSchema.deleteLabel.hidden && !isPM}
                    action={() => {
                      setLabelForDel(label.id);
                    }}
                  />
                </div>
              ))}
            </CollapsibleFormSection>
          ))}
        </section>
      ) : !isPM && (
        <NoContent
          action={() => setShowDrawer(!showDrawer)}
          iconName={'imaging'}
          title={
            <span>
              No <b>Labels</b> here yet! <br />
              Click the button to add a label!
            </span>
          }
          actionText={'Add Label'}
        />
      )}
      <LabelMapper
        close={() => setShowDrawer(false)}
        ctLabels={clinicalTrial?.labelGroups || []}
        open={showDrawer}
      />
      <Modal
        width={440}
        onCancel={() => {
          setLabelForDel('');
        }}
        onOk={deleteLabel}
        okProps={{
          disabled: ctLoading || labelsLoading,
        }}
        okText='Delete'
        centered
        cancelText='Cancel'
        maskClosable={false}
        open={!!labelForDel}
        mask={false}
        okClassName='ant-btn-dangerous'
        classNames={{
          header: 'text-left',
          content: 'text-center !rounded-[2.4rem] !p-[4rem]',
          footer: 'flex justify-between',
        }}
        title='Delete Label'
        titleClassName='text-left'
      >
        Are you sure you want to delete this Label?
        <br />
        This action cannot be undone
      </Modal>
    </div>
  );
};
